﻿import type { ConceptPreliminaryEditionCreate } from 'Concept/components/Concept/Operations/ConceptCreateEditionDialog'
import { ConceptSearchDocument } from 'Contact/api/SearchApi'
import { claimTypesEnum } from 'User/utils/claims'
import { Operation } from 'fast-json-patch'

import { Authentication } from 'Common/auth/types'
import { servicesAsync } from 'Common/environment/environment'
import { SearchResultItem } from 'Common/types'
import { fetchDelete, fetchGet, fetchPatch, fetchPost, fetchPut } from 'Common/utils/query/request'
import { getMyPublishingHouseIdsApprover, getMyUserId } from 'Common/utils/user/auth'

const baseUrl = servicesAsync.concept('')

interface ConceptPatch {
    id: number
    patchSet: Operation[]
}

interface ConceptEditionPost {
    id: number
    edition: ConceptPreliminaryEditionCreate
}

export interface ConceptEditionDelete {
    id: number
    editionId: number
}

interface ConceptMemberPost {
    conceptId: number
    contactId: string
    roleId: string
    accessLevel: string
}

interface ConceptMemberPut {
    conceptId: number
    contactId: number
    accessLevel: string
}

interface ConceptMemberDelete {
    conceptId: number
    contactId: number
}

interface ConceptEditionCostsGet {
    conceptId: number
    editionId: number
}

interface ConceptApprovalPost {
    conceptId: number
    editionIds: number[]
}

interface ConceptEditionApprovalPost {
    conceptId: number
    editionId: number
}

interface ConceptEditionCostsPut {
    conceptId: number
    editionId: number
    data: any
}

interface ConceptEditionPut {
    conceptId: number
    editionId: number
    edition: any
}

interface DefaultDistributionsGet {
    bindingCodeId: string
    publishingHouseId: string
    estimatedSales: number
}

interface ConceptThemaPut {
    id: number
    themaId: string
    ids: string[]
}

const getContactsFilter = (contactIds: readonly (string | number)[]) =>
    contactIds.map((contactId) => `trustedContacts/any(t: t eq '${contactId}')`).join(' or ')

const getApproverFilter = (approverIds: readonly (string | number)[]) =>
    approverIds.map((publishingHouseId) => `publishingHouse eq '${publishingHouseId}'`).join(' or ')

export const conceptsMyGet = async (auth: Authentication) => {
    const userId = getMyUserId(auth)
    const filter = `$filter=(status eq 'active' or status eq 'pendingApproval') and (owner eq '${userId}')`
    const select =
        'members,publishingHouse,accessLevel,contactRoles,title,roles,status,completion,id,owner'
    const response = await fetchGet(
        `${await baseUrl}/v2/search/concepts?${filter}&$orderBy=updated desc&$select=${select}&limit=500`,
    )

    return response.results as SearchResultItem<ConceptSearchDocument>[]
}

export const conceptsShareGet = async (auth: Authentication) => {
    const contactIds = auth.data?.opus_contact_id ?? []
    const approverIds = getMyPublishingHouseIdsApprover(auth, claimTypesEnum.enum.opus_concept)

    let filter = "$filter=(status eq 'active' or status eq 'pendingApproval')"
    let additionalFilters = ''

    if (contactIds.length > 0) {
        additionalFilters = getContactsFilter(contactIds)
    }

    if (approverIds.length > 0) {
        const approverFilter = getApproverFilter(approverIds)
        additionalFilters += `${
            contactIds.length > 0 ? ' or ' : ''
        }((${approverFilter}) and status eq 'pendingApproval')`
    }

    if (additionalFilters.length > 0) {
        filter += ` and (${additionalFilters})`
    }

    const select =
        'members,publishingHouse,accessLevel,contactRoles,title,roles,status,completion,id,owner'

    const response = await fetchGet(
        `${await baseUrl}/v2/search/concepts?${filter}&$orderBy=updated desc&$select=${select}&limit=500`,
    )

    return response.results as SearchResultItem<ConceptSearchDocument>[]
}

export const conceptPatch = async (conceptPatch: ConceptPatch) => {
    return await fetchPatch(
        `${await baseUrl}/v1/concepts/${conceptPatch.id}`,
        JSON.stringify(conceptPatch.patchSet),
    )
}

export const conceptClonePost = async (id: number) => {
    return await fetchPost(`${await baseUrl}/v1/concepts/${id}/clone`, undefined)
}

export const conceptGet = async (id: number) => {
    return await fetchGet(`${await baseUrl}/v1/concepts/${id}`)
}

export const themasConceptGet = async (id: number) => {
    return await fetchGet(`${await baseUrl}/v1/concepts/${id}/themas`)
}

export const conceptMemberPost = async (request: ConceptMemberPost) => {
    return await fetchPost(
        `${await baseUrl}/v1/concepts/${request.conceptId}/members`,
        JSON.stringify({
            id: request.contactId,
            roleId: request.roleId,
            accessLevel: request.accessLevel,
        }),
    )
}

export const conceptMemberPut = async (request: ConceptMemberPut) => {
    return await fetchPut(
        `${await baseUrl}/v1/concepts/${request.conceptId}/members/${request.contactId}`,
        JSON.stringify({
            accessLevel: request.accessLevel,
        }),
    )
}

export const conceptMemberDelete = async (request: ConceptMemberDelete) => {
    return await fetchDelete(
        `${await baseUrl}/v1/concepts/${request.conceptId}/members/${request.contactId}`,
    )
}

export const conceptEditionCostGet = async (request: ConceptEditionCostsGet) => {
    return await fetchGet(
        `${await baseUrl}/v1/concepts/${request.conceptId}/productions/${request.editionId}/costs`,
    )
}

export const conceptEditionCostPut = async (request: ConceptEditionCostsPut) => {
    return await fetchPut(
        `${await baseUrl}/v1/concepts/${request.conceptId}/productions/${request.editionId}/costs`,
        JSON.stringify(request.data),
    )
}

export const conceptPreliminaryEditionPost = async (request: ConceptEditionPost) => {
    return await fetchPost(
        `${await baseUrl}/v1/concepts/${request.id}/edition`,
        JSON.stringify(request.edition),
    )
}

export const conceptPreliminaryEditionSimpleCreate = async (request: ConceptEditionPost) => {
    return await fetchPost(
        `${await baseUrl}/v1/concepts/${request.id}/editionSimple`,
        JSON.stringify(request.edition),
    )
}

export const conceptPreliminaryEditionDelete = async (request: ConceptEditionDelete) => {
    return await fetchDelete(
        `${await baseUrl}/v1/concepts/${request.id}/productions/${request.editionId}`,
    )
}

export const conceptApprovalPost = async (request: ConceptApprovalPost) => {
    return await fetchPost(
        `${await baseUrl}/v1/concepts/${request.conceptId}/approve`,
        JSON.stringify({ conceptEditionIds: request.editionIds }),
    )
}

export const conceptApprovalEditionPost = async (request: ConceptEditionApprovalPost) => {
    return await fetchPost(
        `${await baseUrl}/v1/concepts/${request.conceptId}/approve/edition/${request.editionId}`,
        JSON.stringify(request.editionId),
    )
}

export const conceptEditionPut = async (request: ConceptEditionPut) => {
    const calculatedProduction = {
        ...request.edition,
        audioTotalRunningTimeMinutes:
            +request.edition.audioRunningTimeHours * 60 + +request.edition.audioRunningTimeMinutes,
    }
    return await fetchPut(
        `${await baseUrl}/v1/concepts/${request.conceptId}/productions/${request.editionId}`,
        JSON.stringify(calculatedProduction),
    )
}

export const defaultDistributionsGet = async (request: DefaultDistributionsGet) => {
    return await fetchGet(
        `${await baseUrl}/v1/defaults/distributions?bindingcode=${request.bindingCodeId}&publishinghouseid=${request.publishingHouseId}&estimatedsales=${request.estimatedSales}`,
    )
}

export const conceptPost = async (request: any) => {
    return await fetchPost(`${await baseUrl}/v1/concepts`, JSON.stringify(request))
}

export const conceptThemaPut = async (request: ConceptThemaPut) => {
    return await fetchPut(
        `${await baseUrl}/v1/concepts/${request.id}/themas/${request.themaId}`,
        JSON.stringify(request.ids),
    )
}
